/* ==========================================================================
   vtm-header-megamenu.js

   Adds the two behaviours Glozin's native mega menu does not have, so the
   Categories panel matches ingco.in:

     1. hover-to-swap — the rail on the left highlights the hovered category
        and the right pane shows only that category's children. Glozin
        renders every column at once.
     2. image cards — Glozin's child links are plain text. Each link that
        points at a collection gets that collection's image injected, pulled
        from /collections.json.

   Where a collection carries no image of its own, the card falls back to the
   first product in that collection, fetched lazily the first time the panel
   is opened. A real collection image always wins, and the moment one appears
   it replaces the stand-in — see refreshCollectionImages below.

   Pairs with vtm-header-megamenu.css, which owns all of the layout. This
   file only sets classes and builds card markup.

   Cards render at both breakpoints: a 140x170 image above the label on
   desktop, a 30px thumbnail beside it in the mobile drawer.
   ========================================================================== */

(function () {
  'use strict';

  /* Top-level menu items to convert, matched on their exact title
     (case-insensitive, trimmed).

     RAIL_LABEL   gets the category rail plus swapping image cards.
     BANNER_LABEL gets the banner row — layout only, no rail and no cards,
                  so its content stays whatever the theme block renders. */
  var RAIL_LABEL = 'Categories';
  var BANNER_LABEL = 'Hot Deal';

  var DESKTOP = '(min-width: 1025px)';
  var CACHE_KEY = 'vtm-mm-collections';
  var FALLBACK_KEY = 'vtm-mm-fallback';
  var IMG_WIDTH = 380; /* 2x the 190px card, for retina */

  /* A collection with no image costs one request of its own, so they run a
     few at a time — two dozen parallel fetches out of a hover is not a
     reasonable thing to do to a phone. */
  var FALLBACK_CONCURRENCY = 6;

  var ACTIVE = 'vtm-mm-active';
  var HOOK = 'vtm-mega';
  var HOOK_BANNER = 'vtm-mega-banner';

  /* handle -> [{media, label}] for every card built, so a later answer can
     upgrade the picture in place. */
  var slots = {};

  /* handle -> true for the cards still without any image at all. */
  var pending = {};

  var fallbacksStarted = false;
  var collectionsReady = null;

  /* ---- session cache ---------------------------------------------------- */

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch (e) { /* private mode — start empty */ }
    return null;
  }

  function writeCache(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
  }

  /* handle -> src, where '' records "looked, that collection has nothing" so
     an empty collection is not re-fetched on every page of the session. */
  function readFallbackCache() { return readCache(FALLBACK_KEY) || {}; }
  function writeFallbackCache(map) { writeCache(FALLBACK_KEY, map); }

  /* ---- collection images ------------------------------------------------ */

  /* handle -> image src for every collection that has one. Resolves to null
     rather than {} on failure, so a blip never blanks the cards on screen. */
  function fetchCollectionImages() {
    return fetch('/collections.json?limit=250', {
      headers: { Accept: 'application/json' },
      cache: 'no-cache'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return null;
        var map = {};
        (data.collections || []).forEach(function (c) {
          if (c.image && c.image.src) map[c.handle] = c.image.src;
        });
        writeCache(CACHE_KEY, map);
        return map;
      })
      .catch(function () { return null; });
  }

  /* The session cache is what makes the panel instant on later pages, but on
     its own it also hides a collection image set minutes ago: sessionStorage
     survives a reload, so only a brand new tab would ever pick it up. The
     file is ~7KB and uncached, so it is re-read in the background on every
     load and any card still showing a stand-in is upgraded in place. */
  function refreshCollectionImages() {
    return fetchCollectionImages().then(function (fresh) {
      if (!fresh) return;

      var fallbacks = readFallbackCache();
      var dropped = false;

      Object.keys(slots).forEach(function (handle) {
        var src = fresh[handle];
        if (!src) return;

        /* It has a picture of its own now — no product lookup needed, and the
           product image already borrowed for it is no longer the answer. */
        delete pending[handle];
        if (handle in fallbacks) {
          delete fallbacks[handle];
          dropped = true;
        }
        slots[handle].forEach(function (slot) { setMedia(slot, src); });
      });

      if (dropped) writeFallbackCache(fallbacks);
    });
  }

  function collectionHandle(href) {
    if (!href) return null;
    var m = href.match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function sized(src) {
    if (!src) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + IMG_WIDTH;
  }

  /* ---- first-product fallback ------------------------------------------- */

  function firstProductImage(handle) {
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=1';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var product = data && data.products && data.products[0];
        var image = product && product.images && product.images[0];
        return (image && image.src) || '';
      })
      .catch(function () { return ''; });
  }

  /* Promise pool: `worker` returns a promise per item, `limit` run at once. */
  function runQueue(items, worker, limit) {
    return new Promise(function (resolve) {
      var next = 0;
      var active = 0;

      function pump() {
        if (next >= items.length && active === 0) return resolve();
        while (active < limit && next < items.length) {
          active++;
          worker(items[next++]).then(function () {
            active--;
            pump();
          });
        }
      }

      pump();
    });
  }

  /* Fills every card left without an image. Runs at most once per page. */
  function resolveFallbacks() {
    if (fallbacksStarted) return;
    fallbacksStarted = true;

    /* Wait on the refresh first: a collection that turns out to have its own
       image must not also cost a product lookup. */
    (collectionsReady || Promise.resolve()).then(function () {
      var handles = Object.keys(pending);
      if (!handles.length) return;

      var cache = readFallbackCache();

      return runQueue(handles, function (handle) {
        return firstProductImage(handle).then(function (src) {
          cache[handle] = src;
          delete pending[handle];
          if (src) {
            (slots[handle] || []).forEach(function (slot) { setMedia(slot, src); });
          }
        });
      }, FALLBACK_CONCURRENCY).then(function () { writeFallbackCache(cache); });
    });
  }

  /* ---- card markup ------------------------------------------------------ */

  function cardImage(src, label) {
    var img = document.createElement('img');
    img.src = sized(src);
    img.alt = label;
    img.loading = 'lazy';
    img.width = 140;
    img.height = 170;
    return img;
  }

  /* Swaps a card's picture. Leaves it alone when it already shows this exact
     one, so the background refresh does not make every thumbnail blink. */
  function setMedia(slot, src) {
    var current = slot.media.firstElementChild;
    if (current && current.getAttribute('src') === sized(src)) return;
    slot.media.textContent = '';
    slot.media.appendChild(cardImage(src, slot.label));
  }

  /* Rebuilds each child link as media box + label, matching the reference's
     190x220 card. Runs once per link; the flag keeps re-entry cheap. */
  function buildCards(mega, images) {
    var links = mega.querySelectorAll('ul.sub-children-menu > ul > li > a');
    var fallbacks = readFallbackCache();

    Array.prototype.forEach.call(links, function (a) {
      if (a.hasAttribute('data-vtm-mm-card')) return;
      a.setAttribute('data-vtm-mm-card', '');

      var label = (a.textContent || '').trim();
      var handle = collectionHandle(a.getAttribute('href'));
      var src = images[handle];

      var inner = document.createElement('div');
      inner.className = 'vtm-mm-card__inner';

      var media = document.createElement('div');
      media.className = 'vtm-mm-card__media';

      /* The menu renders more than once, so one handle collects several. */
      var slot = { media: media, label: label };
      if (handle) (slots[handle] = slots[handle] || []).push(slot);

      /* A collection's own image wins; a product image already resolved this
         session beats going back out for it. */
      if (!src && handle && fallbacks[handle]) src = fallbacks[handle];

      if (src) media.appendChild(cardImage(src, label));
      else if (handle && !(handle in fallbacks)) pending[handle] = true;

      var cap = document.createElement('div');
      cap.className = 'vtm-mm-card__label';
      cap.textContent = label;

      inner.appendChild(media);
      inner.appendChild(cap);

      a.textContent = '';
      a.appendChild(inner);
    });
  }

  /* ---- hover swap ------------------------------------------------------- */

  function rows(mega) {
    return mega.querySelectorAll('ul.subchildmenu > li.menu-link.level-1');
  }

  function activate(mega, row) {
    Array.prototype.forEach.call(rows(mega), function (r) {
      r.classList.toggle(ACTIVE, r === row);
    });
  }

  function resetToFirst(mega) {
    var first = rows(mega)[0];
    if (first) activate(mega, first);
  }

  function wire(mega) {
    if (mega.hasAttribute('data-vtm-mm-wired')) return;
    mega.setAttribute('data-vtm-mm-wired', '');

    /* Opening the panel is the cue to go and find the missing images. Doing
       it at page load would spend those requests on every visitor, including
       everyone who never touches the menu. */
    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      mega.addEventListener(evt, resolveFallbacks, { passive: true, once: true });
    });

    Array.prototype.forEach.call(rows(mega), function (row) {
      /* mouseenter, not mouseover: the cards inside would retrigger it. */
      row.addEventListener('mouseenter', function () {
        if (window.matchMedia(DESKTOP).matches) activate(mega, row);
      });
    });

    /* Reopening the panel should always start on the first category, the
       way it does on the reference. */
    mega.addEventListener('mouseleave', function () { resetToFirst(mega); });

    resetToFirst(mega);
  }

  /* ---- boot ------------------------------------------------------------- */

  function labelOf(li) {
    var link = li.querySelector(':scope > menu-item > a');
    if (!link) return '';
    var span = link.querySelector('span');
    return ((span || link).textContent || '').trim().toLowerCase();
  }

  function init() {
    /* Glozin renders the menu more than once (the sticky header carries its
       own copy), so every match has to be handled, not just the first. */
    var all = document.querySelectorAll('li.mega-menu');
    var rails = [];
    var banners = [];

    Array.prototype.forEach.call(all, function (li) {
      var label = labelOf(li);
      if (label === RAIL_LABEL.toLowerCase()) rails.push(li);
      else if (label === BANNER_LABEL.toLowerCase()) banners.push(li);
    });

    /* The banner panel needs no data, only the layout hooks — HOOK carries
       the shared panel width and the mobile accordion, HOOK_BANNER the
       banner-specific rules. */
    banners.forEach(function (mega) {
      mega.classList.add(HOOK, HOOK_BANNER);
    });

    if (!rails.length) return;

    /* The editor re-renders the header, throwing away the elements these
       slots point at. */
    slots = {};
    pending = {};
    fallbacksStarted = false;

    /* Build from the cache straight away so there is no gap where the cards
       are text, then go and check whether anything has changed since. */
    var cached = readCache(CACHE_KEY) || {};
    rails.forEach(function (mega) {
      mega.classList.add(HOOK);
      buildCards(mega, cached);
      wire(mega);
    });

    collectionsReady = refreshCollectionImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the header section on every settings change. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('li.mega-menu')) init();
  });
})();